import type { GridRequestKind, IsoLinesRequestKind, PoiParameterRequestKind } from "@/cache/GeoJSONCache";
import type { WindMetaData } from "@/layers/wind/types";
import type { IconData } from "@/reducer/client-models";
import {
  type CoordinateSystem,
  type GridRequest,
  type IsoLinesRequest,
  type LightningListRequest,
  MeteomaticsApiUrl,
  type PointRequest,
  type WfsRequest,
  type WindVectorRequest,
} from "@mm/api.meteomatics.com";
import { DateTime, Duration } from "luxon";

export const generateGridLayerCacheId = (request: GridRequest<any>, kind: GridRequestKind): string => {
  const url = MeteomaticsApiUrl.forGrid({ format: "json" }, request);
  return `grid${kind}${url.toString()}`;
};

//TODO:
export const generateWindVectorLayerCacheId = (
  request: WindVectorRequest<CoordinateSystem.WGS84>,
  windMetaData: WindMetaData,
): string => {
  const { uMin, uMax, vMin, vMax } = windMetaData;
  const url = MeteomaticsApiUrl.forWindVector({ format: "json" }, request);
  return `wind_vector_${uMin}_${uMax}_${vMin}_${vMax}_${url.toString()}`;
};

export const generateIsoLinesLayerCacheId = (request: IsoLinesRequest, kind: IsoLinesRequestKind): string => {
  const url = MeteomaticsApiUrl.forIsoLines(request);
  return url.toString() + kind;
};
const LIGHTNING_CACHE_BUCKET_MS = Duration.fromObject({ minutes: 5 }).as("milliseconds");

const floorToLightningCacheBucket = (millis: number): number => {
  return Math.floor(millis / LIGHTNING_CACHE_BUCKET_MS) * LIGHTNING_CACHE_BUCKET_MS;
};

const ceilToLightningCacheBucket = (millis: number): number => {
  return Math.ceil(millis / LIGHTNING_CACHE_BUCKET_MS) * LIGHTNING_CACHE_BUCKET_MS;
};

export const canonicalizeLightningWindow = <T extends { datetime: DateTime; timeDuration: Duration }>(
  request: T,
): T => {
  const windowEndMs = request.datetime.toMillis();
  const windowStartMs = windowEndMs - request.timeDuration.as("milliseconds");

  // Floor start and ceil end so canonicalized windows never lose events at edges.
  const canonicalStartMs = floorToLightningCacheBucket(windowStartMs);
  const canonicalEndMs = ceilToLightningCacheBucket(windowEndMs);
  const canonicalDurationMs = Math.max(canonicalEndMs - canonicalStartMs, LIGHTNING_CACHE_BUCKET_MS);

  return {
    ...request,
    datetime: DateTime.fromMillis(canonicalEndMs, { zone: "utc" }),
    timeDuration: Duration.fromMillis(canonicalDurationMs),
  };
};

/**
 * Canonicalize lightning request window to fixed 5-minute buckets.
 * This keeps cache keys stable for +1 minute auto-refresh shifts.
 */
export const canonicalizeLightningRequestWindow = <C extends CoordinateSystem>(
  request: LightningListRequest<C>,
): LightningListRequest<C> => {
  return canonicalizeLightningWindow(request);
};

/**
 * Generates a stable lightning cache id based on canonicalized time windows.
 */
export const generateLightningListLayerCacheId = (request: LightningListRequest<any>): string => {
  const canonical = canonicalizeLightningRequestWindow(request);
  const url = MeteomaticsApiUrl.forLightningList(canonical);
  return url.toString();
};

export const generateWfsCacheId = (request: WfsRequest<any>): string => {
  const url = MeteomaticsApiUrl.forWfs(request);
  return `wfs${url.toString()}`;
};

export const generatePOILayerCacheId = (
  request: PointRequest<any>,
  kind: PoiParameterRequestKind,
  item: IconData,
  optionsSignature = "",
): string => {
  const url = MeteomaticsApiUrl.forPointAndTime({ format: "json" }, request);
  // add IconData info that it reloads on changes
  const iconOptions = [item.text, item.icon, item.color, item.size];
  const coordinatesSignature = item.coords.map((coord) => `${coord.lat},${coord.lon}`).join(";");
  return `multiPoints${kind}${url.toString()}${iconOptions.join("-")}${coordinatesSignature}${optionsSignature}`;
};

export const generatePOILayerCacheIdAdvanced = (request: PointRequest<any>, kind: PoiParameterRequestKind): string => {
  const url = MeteomaticsApiUrl.forPointAndTime({ format: "json" }, request);
  return `multiPoints${kind}${url.toString()}`;
};
